import React from "react";
import { Card, CardContent, Typography, Box, Grid } from "@mui/material";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { VideosUploadedData } from "../../types";

interface VideosUploadedProps {
  data: VideosUploadedData;
  useVerbiage: "state" | "organization" | "nationwide";
  stateOrOrganization: string;
  isExport?: boolean;
}

const VideosUploaded: React.FC<VideosUploadedProps> = ({
  data,
  useVerbiage,
  stateOrOrganization,
  isExport = false,
}) => {
  const getVerbiage = () => {
    let verbiage =
      useVerbiage === "state"
        ? `in ${stateOrOrganization}`
        : useVerbiage === "organization"
        ? `by ${stateOrOrganization}`
        : "nationwide";

    let subtitle =
      useVerbiage === "state"
        ? `Secure video uploads of recorded forensic interviews submitted each month across agencies ${verbiage}.`
        : useVerbiage === "organization"
        ? "Your team securely uploaded video forensic interviews each month."
        : "Secure video uploads of recorded forensic interviews submitted each month across agencies nationwide.";

    return [`Monthly video uploads ${verbiage} in the last 12 months`, subtitle];
  };

  const chartOptions: Highcharts.Options = {
    chart: {
      type: "line",
      backgroundColor: "transparent",
      height: isExport ? 150 : 300,
    },
    exporting: {
      enabled: false,
    },
    title: {
      text: "",
    },
    xAxis: {
      categories: data.monthlyData.map((item) => {
        const [year, month] = item.month.split("-");
        const date = new Date(parseInt(year), parseInt(month) - 1, 1);
        return date.toLocaleString("default", { month: "short" });
      }),
      labels: {
        style: {
          color: "#64748B",
          fontSize: "12px",
        },
      },
      lineColor: "#E2E8F0",
      tickColor: "#E2E8F0",
    },
    yAxis: {
      title: {
        text: "",
      },
      labels: {
        style: {
          color: "#64748B",
          fontSize: "12px",
        },
      },
      gridLineColor: "#F1F5F9",
    },
    legend: {
      enabled: false,
    },
    plotOptions: {
      line: {
        dataLabels: {
          enabled: false,
        },
        marker: {
          enabled: true,
          radius: 4,
        },
      },
    },
    series: [
      {
        type: "line",
        name: "Video Uploads",
        data: data.monthlyData.map((item) => item.uploads),
        color: "#562a75",
        lineWidth: 3,
      },
    ],
    credits: {
      enabled: false,
    },
    tooltip: {
      formatter: function () {
        return `<b>${this.x}</b><br/>Uploads: ${this.y?.toLocaleString()}`;
      },
    },
  };

  return (
    <Card>
      <CardContent
        className={isExport ? "export-card-content" : ""}
        sx={{ p: isExport ? 0 : 3 }}
      >
        <Typography
          variant={isExport ? "body1" : "h5"}
          component="h2"
          sx={{
            mb: 1,
            fontWeight: 600,
            textAlign: isExport ? "center" : "left",
          }}
        >
          Video Evidence Uploaded
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mb: 3, display: isExport ? "none" : "block" }}
        >
          {getVerbiage()[1]}
        </Typography>

        <Grid
          container
          spacing={isExport ? 0 : 3}
          sx={{ mb: isExport ? 1 : 3 }}
        >
          <Grid item xs={6}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Typography
                variant={isExport ? "h5" : "h4"}
                component="div"
                sx={{
                  fontWeight: 700,
                  color: "primary.main",
                  mb: 0.5,
                }}
              >
                {data.totalUploads.toLocaleString()}
              </Typography>
              <Typography
                variant={isExport ? "body2" : "body1"}
                color="text.secondary"
                sx={{ fontSize: isExport ? "0.5rem" : "0.75rem" }}
              >
                Total Uploads (Last 12 Months)
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Typography
                variant={isExport ? "h5" : "h4"}
                component="div"
                sx={{
                  fontWeight: 700,
                  color: "#8A4B9D",
                  mb: 0.5,
                }}
              >
                {data.cumulativeTotal.toLocaleString()}
              </Typography>
              <Typography
                variant={isExport ? "body2" : "body1"}
                color="text.secondary"
                sx={{ fontSize: isExport ? "0.5rem" : "0.75rem" }}
              >
                Cumulative Total
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <HighchartsReact highcharts={Highcharts} options={chartOptions} />

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: isExport ? 0 : 2, textAlign: "center" }}
        >
          {getVerbiage()[0]}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default VideosUploaded;
